"use client";

import React, { useEffect, useRef } from "react";
import * as THREE from "three";
import { OrbitControls } from "three/examples/jsm/controls/OrbitControls.js";
import { EffectComposer } from "three/examples/jsm/postprocessing/EffectComposer.js";
import { RenderPass } from "three/examples/jsm/postprocessing/RenderPass.js";
import { UnrealBloomPass } from "three/examples/jsm/postprocessing/UnrealBloomPass.js";
import { Reflector } from "three/examples/jsm/objects/Reflector.js";
import { GLTFLoader } from "three/examples/jsm/loaders/GLTFLoader.js";
import { DRACOLoader } from "three/examples/jsm/loaders/DRACOLoader.js";

export default function FluidPage() {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    if (!canvasRef.current) return;

    // Load lil-gui dynamically
    let guiInstance: any = null;
    const guiScript = document.createElement("script");
    guiScript.type = "module";
    guiScript.src = "https://cdn.jsdelivr.net/npm/lil-gui@0.19/+esm";

    // ─────────────────────────────────────────────────────────────────
    // MAIN INITIALIZATION
    // ─────────────────────────────────────────────────────────────────
    const canvas = canvasRef.current;
    const renderer = new THREE.WebGLRenderer({ canvas, antialias: true });
    let W = window.innerWidth;
    let H = window.innerHeight;
    renderer.setSize(W, H);
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    renderer.toneMapping = THREE.ACESFilmicToneMapping;
    renderer.toneMappingExposure = 1.0;
    renderer.shadowMap.enabled = true;
    renderer.shadowMap.type = THREE.PCFSoftShadowMap;

    const scene = new THREE.Scene();
    scene.background = new THREE.Color(0x0a0a0c);

    // ─────────────────────────────────────────────────────────────────
    // CAMERA SYSTEM: ORTHOGRAPHIC ISOMETRIC
    // ─────────────────────────────────────────────────────────────────
    const frustumSize = 12;
    let aspect = W / H;
    const cam = new THREE.OrthographicCamera(
      -frustumSize * aspect / 2,
      frustumSize * aspect / 2,
      frustumSize / 2,
      -frustumSize / 2,
      0.1,
      1000
    );
    cam.position.set(10, 10, 10);

    const controls = new OrbitControls(cam, renderer.domElement);
    controls.enableDamping = true;
    controls.dampingFactor = 0.05;
    controls.maxPolarAngle = Math.PI / 2 - 0.05;
    controls.target.set(0, 0, 0);

    const clock = new THREE.Clock();

    // ─────────────────────────────────────────────────────────────────
    // LIGHTING SYSTEM
    // ─────────────────────────────────────────────────────────────────
    const ambientLight = new THREE.AmbientLight(0xffffff, 0.6);
    scene.add(ambientLight);

    const dirLight = new THREE.DirectionalLight(0xffffff, 1.5);
    dirLight.position.set(15, 20, 10);
    dirLight.castShadow = true;
    dirLight.shadow.mapSize.width = 2048;
    dirLight.shadow.mapSize.height = 2048;
    dirLight.shadow.bias = -0.0001;
    dirLight.shadow.camera.left = -20;
    dirLight.shadow.camera.right = 20;
    dirLight.shadow.camera.top = 20;
    dirLight.shadow.camera.bottom = -20;
    dirLight.shadow.camera.near = 0.5;
    dirLight.shadow.camera.far = 100;
    scene.add(dirLight);

    const pointLight = new THREE.PointLight(0xff0055, 3, 15);
    pointLight.position.set(-4, 3, -4);
    scene.add(pointLight);

    const blueLight = new THREE.PointLight(0x00aaff, 3, 15);
    blueLight.position.set(4, 3, 4);
    scene.add(blueLight);

    // ─────────────────────────────────────────────────────────────────
    // PARAMS CONFIG
    // ─────────────────────────────────────────────────────────────────
    const params = {
      dissipation: 0.99,
      velDissipation: 0.9,
      fluidRadius: 0.0134,
      bloomStrength: 0.8,
      bloomRadius: 0.4,
      bloomThreshold: 0.1,
      robotSpeed: 2.5,
      robotRotationOffset: -1.88,
      robotScale: 0.35,
      robotY: 0.2
    };

    // ─────────────────────────────────────────────────────────────────
    // FLUID SIMULATION SETUP (FBO PING-PONG)
    // ─────────────────────────────────────────────────────────────────
    const simRes = 256;
    const optionsFBO: THREE.RenderTargetOptions = {
      minFilter: THREE.LinearFilter,
      magFilter: THREE.LinearFilter,
      format: THREE.RGBAFormat,
      type: THREE.FloatType,
      depthBuffer: false,
      stencilBuffer: false
    };

    let rtVelocityPrev = new THREE.WebGLRenderTarget(simRes, simRes, optionsFBO);
    let rtVelocityCurr = new THREE.WebGLRenderTarget(simRes, simRes, optionsFBO);
    let rtDensityPrev = new THREE.WebGLRenderTarget(simRes, simRes, optionsFBO);
    let rtDensityCurr = new THREE.WebGLRenderTarget(simRes, simRes, optionsFBO);

    const fboScene = new THREE.Scene();
    const fboCam = new THREE.OrthographicCamera(-1, 1, 1, -1, 0, 1);
    const fboMesh = new THREE.Mesh(new THREE.PlaneGeometry(2, 2));
    fboScene.add(fboMesh);

    // Shader 1: Inject mouse/robot force
    const fluidInjectShader = new THREE.ShaderMaterial({
      uniforms: {
        uBuffer: { value: null },
        uMouse: { value: new THREE.Vector2() },
        uDir: { value: new THREE.Vector2() },
        uRadius: { value: params.fluidRadius },
        uStrength: { value: 0.0 }
      },
      vertexShader: `
        varying vec2 vUv;
        void main() {
          vUv = uv;
          gl_Position = vec4(position, 1.0);
        }
      `,
      fragmentShader: `
        uniform sampler2D uBuffer;
        uniform vec2 uMouse;
        uniform vec2 uDir;
        uniform float uRadius;
        uniform float uStrength;
        varying vec2 vUv;
        void main() {
          vec4 base = texture2D(uBuffer, vUv);
          vec2 toPixel = vUv - uMouse;
          float d = length(toPixel);
          float weight = smoothstep(uRadius, 0.0, d);
          vec2 repulsionDir = normalize(toPixel + uDir * 0.3);
          vec2 finalForce = repulsionDir * weight * uStrength;
          gl_FragColor = base + vec4(finalForce, 0.0, weight * uStrength);
        }
      `
    });

    // Shader 2: Advection physics
    const fluidAdvectShader = new THREE.ShaderMaterial({
      uniforms: {
        uVelocity: { value: null },
        uSource: { value: null },
        uDissipation: { value: 0.98 }
      },
      vertexShader: `
        varying vec2 vUv;
        void main() {
          vUv = uv;
          gl_Position = vec4(position, 1.0);
        }
      `,
      fragmentShader: `
        uniform sampler2D uVelocity;
        uniform sampler2D uSource;
        uniform float uDissipation;
        varying vec2 vUv;
        void main() {
          vec2 vel = texture2D(uVelocity, vUv).xy;
          vec2 sourceUv = vUv - vel * 0.003;
          gl_FragColor = texture2D(uSource, sourceUv) * uDissipation;
        }
      `
    });

    // ─────────────────────────────────────────────────────────────────
    // FLOOR: 3 LAYERS
    // ─────────────────────────────────────────────────────────────────

    // Layer 1: Reflector (mirror)
    const reflector = new Reflector(new THREE.PlaneGeometry(100, 100), {
      color: 0x999999,
      textureWidth: W * window.devicePixelRatio,
      textureHeight: H * window.devicePixelRatio
    });
    reflector.rotation.x = -Math.PI / 2;
    reflector.position.y = -0.02;
    scene.add(reflector);

    // Layer 2: Shadow catcher
    const shadowMat = new THREE.MeshPhongMaterial({
      color: 0x000000,
      transparent: true,
      opacity: 0.35,
      depthWrite: false,
    });
    const shadowPlane = new THREE.Mesh(new THREE.PlaneGeometry(100, 100), shadowMat);
    shadowPlane.rotation.x = -Math.PI / 2;
    shadowPlane.position.y = -0.01;
    shadowPlane.receiveShadow = true;
    scene.add(shadowPlane);

    // Layer 3: Fluid overlay
    const floorGeo = new THREE.PlaneGeometry(100, 100);
    const floorMat = new THREE.ShaderMaterial({
      transparent: true,
      depthWrite: false,
      uniforms: {
        uFluidTexture: { value: null },
        uTime: { value: 0 },
        uBaseColor: { value: new THREE.Color(0x0a0a14) }
      },
      vertexShader: `
        varying vec2 vUv;
        varying vec3 vWorldPos;
        void main() {
          vUv = uv;
          vec4 worldPos = modelMatrix * vec4(position, 1.0);
          vWorldPos = worldPos.xyz;
          gl_Position = projectionMatrix * viewMatrix * worldPos;
        }
      `,
      fragmentShader: `
        uniform sampler2D uFluidTexture;
        uniform vec3 uBaseColor;
        uniform float uTime;
        varying vec2 vUv;
        varying vec3 vWorldPos;

        vec3 oilPalette(in float t) {
          vec3 a = vec3(0.5, 0.5, 0.5);
          vec3 b = vec3(0.5, 0.5, 0.5);
          vec3 c = vec3(1.0, 1.0, 1.0);
          vec3 d = vec3(0.0, 0.33, 0.67);
          return a + b * cos(6.28318 * (c * t + d));
        }

        vec3 mod289(vec3 x) { return x - floor(x * (1.0 / 289.0)) * 289.0; }
        vec2 mod289(vec2 x) { return x - floor(x * (1.0 / 289.0)) * 289.0; }
        vec3 permute(vec3 x) { return mod289(((x*34.0)+1.0)*x); }
        float snoise(vec2 v) {
          const vec4 C = vec4(0.211324865405187, 0.366025403784439, -0.577350269189626, 0.024390243902439);
          vec2 i  = floor(v + dot(v, C.yy)); vec2 x0 = v - i + dot(i, C.xx);
          vec2 o1 = (x0.x > x0.y) ? vec2(1.0,0.0) : vec2(0.0,1.0);
          vec4 x12 = x0.xyxy + C.xxzz; x12.xy -= o1; i = mod289(i);
          vec3 p = permute(permute(i.y + vec3(0.0, o1.y, 1.0)) + i.x + vec3(0.0, o1.x, 1.0));
          vec3 m = max(0.5 - vec3(dot(x0,x0), dot(x12.xy,x12.xy), dot(x12.zw,x12.zw)), 0.0);
          m = m*m; m = m*m; vec3 x = 2.0 * fract(p * C.www) - 1.0; vec3 h = abs(x) - 0.5;
          vec3 ox = floor(x + 0.5); vec3 a0 = x - ox; m *= 1.79284291400159 - 0.85373472095314 * (a0*a0 + h*h);
          vec3 g; g.x  = a0.x  * x0.x  + h.x  * x0.y; g.yz = a0.yz * x12.xz + h.yz * x12.yw;
          return 130.0 * dot(m, g);
        }

        void main() {
          vec4 fluid = texture2D(uFluidTexture, vUv);
          float density = smoothstep(0.05, 0.95, fluid.w);
          vec2 velocity = fluid.xy;
          float microNoise = snoise(vUv * 25.0 + velocity * 5.0) * 0.08 * smoothstep(0.0, 0.2, density);
          float distortedDensity = clamp(density + microNoise, 0.0, 1.0);
          float colorMap = distortedDensity * 1.5 + uTime * 0.05;
          vec3 oilColor = oilPalette(colorMap);
          float dfx = dFdx(distortedDensity);
          float dfy = dFdy(distortedDensity);
          float edge = length(vec2(dfx, dfy)) * 12.0;
          float gloss = pow(smoothstep(0.1, 0.9, edge), 2.0) * 0.6;
          vec2 worldPosXZ = vWorldPos.xz;
          vec2 gridUv = worldPosXZ * 0.5;
          vec2 gridLine = abs(fract(gridUv - 0.5) - 0.5);
          float lineWidth = 0.02;
          float grid = smoothstep(lineWidth, 0.0, min(gridLine.x, gridLine.y));
          float gridDist = length(worldPosXZ);
          float gridFade = smoothstep(30.0, 5.0, gridDist);
          vec3 gridColor = vec3(0.06, 0.15, 0.35) * grid * gridFade;
          vec3 finalColor = mix(uBaseColor + gridColor, oilColor, smoothstep(0.03, 0.25, distortedDensity));
          finalColor += vec3(1.0) * gloss * smoothstep(0.1, 1.0, distortedDensity);
          float gridAlpha = grid * gridFade * 0.35;
          float fluidAlpha = smoothstep(0.01, 0.2, distortedDensity) * 0.85;
          float alpha = max(gridAlpha, fluidAlpha);
          gl_FragColor = vec4(finalColor, alpha);
        }
      `
    });

    const floorOverlay = new THREE.Mesh(floorGeo, floorMat);
    floorOverlay.rotation.x = -Math.PI / 2;
    floorOverlay.position.y = 0.0;
    scene.add(floorOverlay);

    // ─────────────────────────────────────────────────────────────────
    // RAYCASTING, MOUSE & IDLE SYSTEM
    // ─────────────────────────────────────────────────────────────────
    const raycaster = new THREE.Raycaster();
    const mouse2D = new THREE.Vector2();
    const floorMouseUV = new THREE.Vector2(-1, -1);
    const lastFloorMouseUV = new THREE.Vector2(-1, -1);
    const mouseDir = new THREE.Vector2();

    let isMouseIdle = false;
    let lastMouseMoveTime = Date.now();
    const idleTimeout = 3000;

    const handleMouseMove = (e: MouseEvent) => {
      mouse2D.x = (e.clientX / window.innerWidth) * 2 - 1;
      mouse2D.y = -(e.clientY / window.innerHeight) * 2 + 1;
      lastMouseMoveTime = Date.now();
      isMouseIdle = false;
    };
    window.addEventListener("mousemove", handleMouseMove);

    // ─────────────────────────────────────────────────────────────────
    // ROBOT LOADER & ANIMATION SYSTEM
    // ─────────────────────────────────────────────────────────────────
    const draco = new DRACOLoader();
    draco.setDecoderPath("https://www.gstatic.com/draco/versioned/decoders/1.5.7/");

    const loader = new GLTFLoader();
    loader.setDRACOLoader(draco);

    let robot: THREE.Object3D | null = null;
    let mixer: THREE.AnimationMixer | null = null;
    const actions: Record<string, THREE.AnimationAction> = {};
    const robotTarget = new THREE.Vector3(0, 0, 0);
    let hasTarget = false;
    let currentBaseAction = "iddle";

    function setRandomTarget() {
      const range = 5;
      robotTarget.set(
        (Math.random() - 0.5) * range * 2,
        params.robotY,
        (Math.random() - 0.5) * range * 2
      );
      hasTarget = true;
    }

    function setWeight(action: THREE.AnimationAction, weight: number) {
      action.enabled = true;
      action.setEffectiveTimeScale(1);
      action.setEffectiveWeight(weight);
    }

    function executeCrossFade(startAction: THREE.AnimationAction | null, endAction: THREE.AnimationAction, duration: number) {
      if (!endAction) return;
      setWeight(endAction, 1);
      endAction.time = 0;
      if (startAction) {
        startAction.crossFadeTo(endAction, duration, true);
      } else {
        endAction.fadeIn(duration);
      }
    }

    function prepareCrossFade(nextActionName: string, duration = 0.35) {
      const nextAction = actions[nextActionName];
      if (!nextAction || currentBaseAction === nextActionName) return;
      const currentAction = actions[currentBaseAction];
      executeCrossFade(currentAction, nextAction, duration);
      currentBaseAction = nextActionName;
    }

    loader.load("/models/assets/robot.glb", (gltf) => {
      robot = gltf.scene;
      robot.position.set(0, params.robotY, 0);
      robot.scale.setScalar(params.robotScale);
      robot.rotation.y = params.robotRotationOffset;

      robot.traverse((child) => {
        if ((child as THREE.Mesh).isMesh) {
          child.castShadow = true;
          child.receiveShadow = true;
        }
      });

      scene.add(robot);

      mixer = new THREE.AnimationMixer(robot);
      gltf.animations.forEach((clip) => {
        const name = clip.name.toLowerCase();
        actions[name] = mixer!.clipAction(clip);
      });

      const iddleKey = Object.keys(actions).find(k => k.includes("iddle"));
      const walkKey = Object.keys(actions).find(k => k.includes("walk") || k.includes("run"));

      if (iddleKey) actions["iddle"] = actions[iddleKey];
      if (walkKey) actions["walk"] = actions[walkKey];

      if (actions["iddle"]) {
        setWeight(actions["iddle"], 1);
        actions["iddle"].play();
        currentBaseAction = "iddle";
      }
      if (actions["walk"]) {
        setWeight(actions["walk"], 0);
        actions["walk"].play();
      }
    });

    // ─────────────────────────────────────────────────────────────────
    // POST PROCESSING EFFECT (COMPOSER)
    // ─────────────────────────────────────────────────────────────────
    const composer = new EffectComposer(renderer);
    composer.addPass(new RenderPass(scene, cam));

    const bloomPass = new UnrealBloomPass(new THREE.Vector2(W, H), params.bloomStrength, params.bloomRadius, params.bloomThreshold);
    composer.addPass(bloomPass);

    // GUI (async load lil-gui)
    import("https://cdn.jsdelivr.net/npm/lil-gui@0.19/+esm" as any).then((guiModule: any) => {
      const GUI = guiModule.default || guiModule.GUI || guiModule;
      const gui = new GUI({ title: "Control Panel" });

      const fluidFolder = gui.addFolder("Fluid Settings");
      fluidFolder.add(params, "dissipation", 0.9, 0.99, 0.001).name("Ink Duration");
      fluidFolder.add(params, "velDissipation", 0.8, 0.98, 0.001).name("Flow Damping");
      fluidFolder.add(params, "fluidRadius", 0.01, 0.1, 0.001).name("Brush Size").onChange((v: number) => { fluidInjectShader.uniforms.uRadius.value = v; });

      const bloomFolder = gui.addFolder("Bloom Glow");
      bloomFolder.add(params, "bloomStrength", 0, 3, 0.05).name("Intensity").onChange((v: number) => { bloomPass.strength = v; });
      bloomFolder.add(params, "bloomRadius", 0, 1, 0.05).name("Radius").onChange((v: number) => { bloomPass.radius = v; });
      bloomFolder.add(params, "bloomThreshold", 0, 1, 0.05).name("Threshold").onChange((v: number) => { bloomPass.threshold = v; });

      const robotFolder = gui.addFolder("Robot Adjustments");
      robotFolder.add(params, "robotSpeed", 0.5, 10.0, 0.1).name("Speed");
      robotFolder.add(params, "robotRotationOffset", -Math.PI, Math.PI, 0.01).name("Rotation Offset").onChange((v: number) => {
        if (robot && currentBaseAction === "iddle") robot.rotation.y = v;
      });
      robotFolder.add(params, "robotScale", 0.05, 2.0, 0.01).name("Scale").onChange((v: number) => {
        if (robot) robot.scale.setScalar(v);
      });
      robotFolder.add(params, "robotY", -1, 2, 0.01).name("Height (Y)").onChange((v: number) => {
        if (robot) robot.position.y = v;
      });

      guiInstance = gui;
    }).catch(() => {});

    // ─────────────────────────────────────────────────────────────────
    // FLUID INJECTION HELPER
    // ─────────────────────────────────────────────────────────────────
    function injectFluid(uv: THREE.Vector2, direction: THREE.Vector2, strength: number, radiusFactor: number) {
      (fboMesh as any).material = fluidInjectShader;

      fluidInjectShader.uniforms.uBuffer.value = rtVelocityPrev.texture;
      fluidInjectShader.uniforms.uMouse.value.copy(uv);
      fluidInjectShader.uniforms.uDir.value.copy(direction).multiplyScalar(0.5);
      fluidInjectShader.uniforms.uRadius.value = params.fluidRadius * radiusFactor;
      fluidInjectShader.uniforms.uStrength.value = strength * 2.5;
      renderer.setRenderTarget(rtVelocityCurr);
      renderer.render(fboScene, fboCam);

      fluidInjectShader.uniforms.uBuffer.value = rtDensityPrev.texture;
      fluidInjectShader.uniforms.uDir.value.set(0, 0);
      fluidInjectShader.uniforms.uRadius.value = params.fluidRadius * radiusFactor;
      fluidInjectShader.uniforms.uStrength.value = strength * 1.5;
      renderer.setRenderTarget(rtDensityCurr);
      renderer.render(fboScene, fboCam);

      const tempV = rtVelocityPrev; rtVelocityPrev = rtVelocityCurr; rtVelocityCurr = tempV;
      const tempD = rtDensityPrev; rtDensityPrev = rtDensityCurr; rtDensityCurr = tempD;
    }

    // ─────────────────────────────────────────────────────────────────
    // ANIMATION & RENDERING LOOP
    // ─────────────────────────────────────────────────────────────────
    let elapsedTime = 0;
    let reqId: number;

    function animate() {
      reqId = requestAnimationFrame(animate);

      const dt = Math.min(clock.getDelta(), 0.1);
      elapsedTime += dt;

      if (mixer) mixer.update(dt);

      // Check idle
      if (Date.now() - lastMouseMoveTime > idleTimeout) {
        isMouseIdle = true;
      }

      let injectStrength = 0.0;

      if (!isMouseIdle) {
        // ACTIVE MODE: Robot chases mouse
        raycaster.setFromCamera(mouse2D, cam);
        const intersects = raycaster.intersectObject(floorOverlay);

        if (intersects.length > 0) {
          floorMouseUV.copy(intersects[0].uv!);
          robotTarget.copy(intersects[0].point);
          robotTarget.y = params.robotY;
          hasTarget = true;

          if (lastFloorMouseUV.x !== -1) {
            mouseDir.subVectors(floorMouseUV, lastFloorMouseUV);
            const dist = mouseDir.length();
            if (dist > 0) {
              mouseDir.normalize();
              injectStrength = Math.min(dist * 40.0, 8.0);
            }
          }
          lastFloorMouseUV.copy(floorMouseUV);
        } else {
          injectStrength = 0.0;
        }
      } else {
        // IDLE MODE: Random target roaming
        if (robot && (!hasTarget || robot.position.distanceTo(robotTarget) < 0.2)) {
          setRandomTarget();
        }
      }

      // ROBOT MOVEMENT
      const moveDir = new THREE.Vector3();
      let robotIsWalking = false;

      if (robot) {
        const distToTarget = robot.position.distanceTo(robotTarget);
        if (hasTarget && distToTarget > 0.15) {
          robotIsWalking = true;

          moveDir.subVectors(robotTarget, robot.position).normalize();
          const targetAngle = Math.atan2(moveDir.x, moveDir.z) + params.robotRotationOffset;

          let angleDiff = targetAngle - robot.rotation.y;
          angleDiff = Math.atan2(Math.sin(angleDiff), Math.cos(angleDiff));
          robot.rotation.y += angleDiff * 0.15;

          const speed = params.robotSpeed;
          robot.position.addScaledVector(moveDir, Math.min(speed * dt / 2, distToTarget));

          prepareCrossFade("walk", 0.35);
          if (actions["walk"]) {
            const baseSpeed = 1.85;
            actions["walk"].setEffectiveTimeScale(speed / baseSpeed);
          }
        } else {
          prepareCrossFade("iddle", 0.35);
        }
      }

      // FLUID SIMULATION
      if (!isMouseIdle && injectStrength > 0.0) {
        injectFluid(floorMouseUV, mouseDir, injectStrength, 1.0);
      }

      if (robot && robotIsWalking) {
        const robotUV = new THREE.Vector2(
          (robot.position.x + 50) / 100,
          (-robot.position.z + 50) / 100
        );
        const robotDir2D = new THREE.Vector2(moveDir.x, -moveDir.z).normalize();
        const robotInjectStrength = 3.0 * dt * params.robotSpeed;
        injectFluid(robotUV, robotDir2D, robotInjectStrength, 0.7);
      }

      // Advection pass
      (fboMesh as any).material = fluidAdvectShader;

      fluidAdvectShader.uniforms.uVelocity.value = rtVelocityPrev.texture;
      fluidAdvectShader.uniforms.uSource.value = rtVelocityPrev.texture;
      fluidAdvectShader.uniforms.uDissipation.value = params.velDissipation;
      renderer.setRenderTarget(rtVelocityCurr);
      renderer.render(fboScene, fboCam);

      fluidAdvectShader.uniforms.uVelocity.value = rtVelocityPrev.texture;
      fluidAdvectShader.uniforms.uSource.value = rtDensityPrev.texture;
      fluidAdvectShader.uniforms.uDissipation.value = params.dissipation;
      renderer.setRenderTarget(rtDensityCurr);
      renderer.render(fboScene, fboCam);

      const tempV2 = rtVelocityPrev; rtVelocityPrev = rtVelocityCurr; rtVelocityCurr = tempV2;
      const tempD2 = rtDensityPrev; rtDensityPrev = rtDensityCurr; rtDensityCurr = tempD2;

      renderer.setRenderTarget(null);

      floorMat.uniforms.uFluidTexture.value = rtDensityPrev.texture;
      floorMat.uniforms.uTime.value = elapsedTime;

      controls.update();
      composer.render();
    }

    animate();

    // ─────────────────────────────────────────────────────────────────
    // RESPONSIVE RESIZE
    // ─────────────────────────────────────────────────────────────────
    const handleResize = () => {
      W = window.innerWidth;
      H = window.innerHeight;
      renderer.setSize(W, H);
      composer.setSize(W, H);

      aspect = W / H;
      (cam as THREE.OrthographicCamera).left = -frustumSize * aspect / 2;
      (cam as THREE.OrthographicCamera).right = frustumSize * aspect / 2;
      (cam as THREE.OrthographicCamera).top = frustumSize / 2;
      (cam as THREE.OrthographicCamera).bottom = -frustumSize / 2;
      cam.updateProjectionMatrix();

      reflector.getRenderTarget().setSize(W * window.devicePixelRatio, H * window.devicePixelRatio);
    };
    window.addEventListener("resize", handleResize);

    return () => {
      cancelAnimationFrame(reqId);
      window.removeEventListener("mousemove", handleMouseMove);
      window.removeEventListener("resize", handleResize);
      if (guiInstance) guiInstance.destroy();
      renderer.dispose();
    };
  }, []);

  return (
    <div style={{ width: "100vw", height: "100vh", overflow: "hidden", background: "#000", color: "#fff", fontFamily: "sans-serif" }}>
      <style>{`
        *,*::before,*::after{margin:0;padding:0;box-sizing:border-box;}
        html,body{width:100%;height:100%;overflow:hidden;background:#000000;color:#fff;font-family:sans-serif;}
        #c{position:fixed;inset:0;width:100%;height:100%;display:block;z-index:1;}
        #ui{position:fixed;inset:0;pointer-events:none;z-index:10;display:flex;flex-direction:column;justify-content:space-between;padding:30px;}
        .header{display:flex;justify-content:space-between;align-items:center;font-size:12px;letter-spacing:0.15em;font-weight:bold;}
        .bottom-title{text-align:center;margin-bottom:20px;}
        .bottom-title h1{font-size:clamp(32px,5vw,64px);font-weight:800;letter-spacing:-0.02em;line-height:0.95;text-transform:uppercase;}
        .accent{color:#ff1d1d;text-shadow:0 0 20px rgba(255,29,29,0.4);}
        .instruction{font-size:11px;opacity:0.5;margin-top:10px;letter-spacing:0.05em;}
      `}</style>

      <canvas ref={canvasRef} id="c" />

      <div id="ui">
        <div className="header">
          <div>FLUID&nbsp;•&nbsp;LAB</div>
          <div>60 FPS // STABLE</div>
        </div>
        <div className="bottom-title">
          <h1>Liquid <span className="accent">Simulation</span></h1>
          <div className="instruction">Gerakkan mouse Anda di atas layar atau diamkan selama 3 detik untuk mode auto-roaming</div>
        </div>
      </div>
    </div>
  );
}
